import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useShortcuts } from "@/context/ShortcutsContext";
import { displayCombo } from "@/lib/keyboardDisplay";
import { Plus, PencilSimple, X, FloppyDisk, Trash } from "@phosphor-icons/react";

// v1.28.70 — Inline shortcut editor an Admin can open next to any menu
// item / pill. Renders a small icon that, when clicked, pops a Ctrl+A..Z
// and Alt+A..Z keyboard grid; keys taken by OTHER actions are disabled;
// clicking any available key picks it and Save PUTs the merged shortcut
// list, then fires `micm-shortcuts-updated` so labels refresh everywhere.
// v1.29.04 — Access widened from Super-User-only to any Admin (Company
// Admin + Super User). DEO / Party accounts still don't see the icons.

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const BLOCKED = new Set(["CTRL+C","CTRL+V","CTRL+X","CTRL+A","CTRL+Z","CTRL+Y","CTRL+T","CTRL+W","CTRL+N","CTRL+S","CTRL+F"]);

export default function InlineShortcutAssigner({ action_id, label, target_route = "", category = "Menu" }) {
    const { user } = useAuth();
    const { shortcuts, refresh } = useShortcuts();
    const [open, setOpen] = useState(false);
    const current = shortcuts.find((s) => s.action_id === action_id);
    const [pick, setPick] = useState(current?.combo || "");
    const [busy, setBusy] = useState(false);

    // v1.29.04 — Any admin role (Super User OR Company Admin) can assign
    // shortcuts. DEO ("data_entry_operator" / role=staff) and Party
    // (role=customer) still get null so the pencil never renders for them.
    const canManage = !!(user?.is_super_user || user?.role === "admin");
    if (!canManage) return null;

    // Combos already used by OTHER actions
    const takenByOther = new Map();
    shortcuts.forEach((s) => {
        if (s.combo && s.action_id !== action_id) takenByOther.set(s.combo.toUpperCase(), s);
    });

    const toggle = (e) => { e.stopPropagation(); e.preventDefault(); setOpen((v) => !v); setPick(current?.combo || ""); };

    // v1.28.71 — Conflict preview: when the picked combo is used by another
    // action, offer a one-tap "Steal from …" that clears the other action
    // and assigns the combo to THIS one in a single PUT.
    const pickConflict = pick ? takenByOther.get(pick.toUpperCase()) : null;

    const save = async (newCombo, options = {}) => {
        const stealFrom = options.stealFrom;
        // Merge: replace the row for this action_id AND optionally clear
        // the row for the action we're stealing the combo from.
        let list = shortcuts
            .filter((s) => s.action_id !== action_id)
            .map((s) => ({ ...s, combo: (stealFrom && s.action_id === stealFrom) ? "" : s.combo }));
        if (newCombo) {
            list.push({ action_id, label, category: category || current?.category || "Menu",
                        combo: newCombo, target_route: target_route || current?.target_route || "" });
        }
        setBusy(true);
        try {
            await api.put("/shortcuts", { shortcuts: list });
            const shown = displayCombo(newCombo);
            toast.success(newCombo
                ? (stealFrom ? `${shown} moved to ${label}` : `Assigned ${shown} to ${label}`)
                : `Removed shortcut for ${label}`);
            window.dispatchEvent(new CustomEvent("micm-shortcuts-updated"));
            refresh();
            setOpen(false);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <span className="relative inline-flex" onClick={(e) => e.stopPropagation()}>
            <button
                type="button"
                onClick={toggle}
                title={current?.combo ? `Edit shortcut (${current.combo})` : "Assign shortcut"}
                data-testid={`sc-assign-${action_id}`}
                className={`ml-1 inline-flex h-4 w-4 items-center justify-center rounded-sm text-[10px] ${current?.combo ? "text-indigo-600 hover:bg-indigo-50" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"}`}
            >
                {current?.combo ? <PencilSimple size={10} weight="bold" /> : <Plus size={10} weight="bold" />}
            </button>
            {open && (
                // v1.28.86 — Assign Shortcut popup rebuilt as a viewport-fixed
                // modal (backdrop + centered panel, z-[9999]) so parent
                // containers on the Keyboard Shortcuts admin page (section
                // cards with overflow:hidden, tables, sheets) can never clip
                // the keyboard grid or the Save/Remove buttons. Escape or
                // backdrop click dismisses without saving.
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4"
                    onClick={toggle}
                    onKeyDown={(e) => { if (e.key === "Escape") toggle(e); }}
                    role="presentation"
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        data-testid={`sc-panel-${action_id}`}
                        className="w-[320px] max-w-[calc(100vw-24px)] max-h-[calc(100vh-32px)] overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                    <div className="mb-2 flex items-start justify-between gap-2">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Assign Shortcut</div>
                            <div className="text-sm font-semibold text-slate-900">{label}</div>
                        </div>
                        <button type="button" onClick={toggle} data-testid={`sc-close-${action_id}`} className="rounded p-0.5 text-slate-400 hover:bg-slate-100"><X size={12} /></button>
                    </div>
                    {["Ctrl", "Alt"].map((mod) => (
                        <div key={mod} className="mb-2">
                            <div className="mb-1 text-[9px] font-bold uppercase tracking-wider text-slate-400">{displayCombo(mod + "+X").replace(/X$/, "")} + …</div>
                            <div className="grid grid-cols-9 gap-0.5">
                                {LETTERS.map((L) => {
                                    const combo = `${mod}+${L}`;
                                    const key = combo.toUpperCase();
                                    const blocked = BLOCKED.has(key);
                                    const takenRow = takenByOther.get(key);
                                    const isCurrent = (current?.combo || "").toUpperCase() === key;
                                    const isPicked = pick.toUpperCase() === key;
                                    // Taken keys stay CLICKABLE now so SU can preview + steal;
                                    // only browser-blocked keys are disabled.
                                    const disabled = blocked;
                                    const cls = isPicked
                                        ? "bg-indigo-600 text-white"
                                        : isCurrent
                                            ? "bg-emerald-500 text-white"
                                            : blocked
                                                ? "bg-slate-100 text-slate-300 cursor-not-allowed"
                                                : takenRow
                                                    ? "bg-amber-100 text-amber-800 hover:bg-amber-200 ring-1 ring-amber-300"
                                                    : "bg-emerald-50 text-emerald-800 hover:bg-emerald-200";
                                    return (
                                        <button
                                            key={combo}
                                            type="button"
                                            disabled={disabled}
                                            onClick={() => setPick(combo)}
                                            title={blocked ? "Reserved by browser" : (takenRow ? `Used by ${takenRow.label} — click to preview & steal` : combo)}
                                            data-testid={`sc-key-${action_id}-${key}`}
                                            className={`h-6 w-6 rounded text-[10px] font-bold uppercase transition ${cls}`}
                                        >
                                            {L}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                    {pickConflict && (
                        <div className="mb-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-[10px] text-amber-900"
                             data-testid={`sc-conflict-${action_id}`}>
                            <div className="font-semibold">
                                <kbd className="rounded bg-white/60 px-1 font-mono">{displayCombo(pick)}</kbd> is used by <b>{pickConflict.label}</b>.
                            </div>
                            <button
                                type="button"
                                disabled={busy}
                                onClick={() => save(pick, { stealFrom: pickConflict.action_id })}
                                data-testid={`sc-steal-${action_id}`}
                                className="mt-1 inline-flex items-center gap-1 rounded bg-amber-600 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                            >
                                Reassign to {label} instead
                            </button>
                        </div>
                    )}
                    <div className="mt-2 flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
                        <div className="text-[10px] text-slate-500">
                            {pick ? <>Selected: <kbd className="rounded bg-slate-100 px-1 font-mono text-[10px]">{displayCombo(pick)}</kbd></> : "Click any green key"}
                        </div>
                        <div className="flex gap-1">
                            {current?.combo && (
                                <button type="button" disabled={busy} onClick={() => save("")}
                                        data-testid={`sc-remove-${action_id}`}
                                        className="inline-flex items-center gap-1 rounded bg-rose-50 px-2 py-1 text-[10px] font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50">
                                    <Trash size={10} weight="bold" /> Remove
                                </button>
                            )}
                            <button type="button" disabled={busy || !pick || pick === current?.combo || !!pickConflict} onClick={() => save(pick)}
                                    data-testid={`sc-save-${action_id}`}
                                    className="inline-flex items-center gap-1 rounded bg-indigo-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-indigo-700 disabled:opacity-40">
                                <FloppyDisk size={10} weight="bold" /> Save
                            </button>
                        </div>
                    </div>
                    </div>
                </div>
            )}
        </span>
    );
}
